import React, { useState, useEffect, useCallback } from 'react';
import { workflowsApi } from '../services/api';
import WorkflowProgressBar from './WorkflowProgressBar';
import WorkflowTimeline from './WorkflowTimeline';

const STATUS_BADGE = {
  running:   { cls: 'status-info',    label: 'En cours' },
  completed: { cls: 'status-success', label: 'Terminé' },
  cancelled: { cls: 'status-error',   label: 'Annulé' },
};

const fmtDuration = (min) => {
  const m = Number(min) || 0;
  if (m <= 0) return '0 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h} h ${r}` : `${h} h`;
};

export default function WorkflowExecutionView({ executionId, user, onBack }) {
  const [execution, setExecution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await workflowsApi.getExecution(executionId);
      setExecution(data.execution);
    } catch {
      alert('Exécution introuvable');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [executionId, onBack]);

  useEffect(() => { load(); }, [load]);

  // Toute mutation renvoie l'exécution complète recalculée (backend autoritaire).
  const run = async (fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setExecution(data.execution);
    } catch (e) {
      alert(e?.response?.data?.error || 'Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  const mutateStep = (stepId, action) => run(() => workflowsApi.mutateStep(executionId, stepId, action));
  const setNote = (stepId, note) => run(() => workflowsApi.setNote(executionId, stepId, note));
  const setCondition = (stepId, checked) => run(() => workflowsApi.setCondition(executionId, stepId, checked));
  const answerDecision = (decisionId, answerId) => run(() => workflowsApi.answerDecision(executionId, decisionId, answerId));
  const cancel = () => {
    if (!window.confirm('Annuler définitivement cette exécution ?')) return;
    run(() => workflowsApi.cancelExecution(executionId));
  };

  if (loading || !execution) return <div className="wf-loading">Chargement…</div>;
  const b = STATUS_BADGE[execution.status] || STATUS_BADGE.running;

  return (
    <div className="wf-exec wf-flow">
      <div className="wf-editor-bar">
        <button className="wf-act-ghost" onClick={onBack}>← Retour</button>
        {execution.status === 'running' && (
          <button className="wf-act-ghost" onClick={cancel} disabled={busy}>Annuler</button>
        )}
      </div>

      <div className="wf-exec-head">
        <div className="wf-exec-titlerow">
          <h2>{execution.name}</h2>
          <span className={`wf-exec-status ${execution.status}`}>{b.label}</span>
        </div>
        <div className="wf-exec-meta">
          <span style={{ color: execution.template_color || 'var(--text-secondary)' }}>● {execution.template_name}</span>
          {execution.societe && <span>🏢 {execution.societe}</span>}
          {execution.entity_type && <span>🔗 {execution.entity_type} {execution.entity_id}</span>}
        </div>
        <div className="wf-exec-progress">
          <WorkflowProgressBar pct={execution.progress_pct} color={execution.template_color} />
          <span className="wf-exec-progress-text">{execution.steps_done}/{execution.steps_total} · {fmtDuration(execution.remaining_minutes)} restant</span>
        </div>
      </div>

      <WorkflowTimeline
        execution={execution}
        busy={busy}
        readOnly={execution.status === 'cancelled'}
        onMutateStep={mutateStep}
        onSetNote={setNote}
        onSetCondition={setCondition}
        onAnswer={answerDecision}
      />
    </div>
  );
}
